import { html } from 'htm/preact';
import { useState } from '@bpmn-io/properties-panel/preact/hooks';
import { is } from 'bpmn-js/lib/util/ModelUtil';

/** Safe wrapper — canvas may not be initialized or may be destroyed. */
function safeGetRootElements(canvas) {
  try { return canvas.getRootElements?.() ?? []; } catch { return []; }
}
function safeGetRootElement(canvas) {
  try { return canvas.getRootElement?.(); } catch { return null; }
}

// Which tree nodes are expanded. Module-level so the state survives the
// properties-panel re-rendering/remounting the component.
const expandedNodes = new Set();

/**
 * Build a tree of sub-processes from the element registry.
 */
function buildTree(elementRegistry) {
  const nodes = new Map();

  elementRegistry.forEach((el) => {
    const bo = el.businessObject;
    if (bo?.$instanceOf?.('bpmn:SubProcess')) {
      const planeId = el.id.endsWith('_plane') ? el.id : el.id + '_plane';
      nodes.set(bo.id, {
        id: el.id,
        boId: bo.id,
        name: bo.name || bo.id,
        planeId,
        children: [],
        parentBoId: null,
      });
    }
  });

  for (const node of nodes.values()) {
    const el = elementRegistry.get(node.id);
    const parentBo = el?.businessObject?.$parent;
    if (parentBo && nodes.has(parentBo.id)) {
      node.parentBoId = parentBo.id;
    }
  }

  const roots = [];
  for (const node of nodes.values()) {
    if (node.parentBoId && nodes.has(node.parentBoId)) {
      nodes.get(node.parentBoId).children.push(node);
    } else {
      roots.push(node);
    }
  }

  return { roots, nodes };
}

/** Walk every node of the tree. */
function forEachNode(roots, fn) {
  for (const node of roots) {
    fn(node);
    forEachNode(node.children, fn);
  }
}

/** Expand the chain leading to (and including) the active node so it is visible. */
function expandActivePath(nodes, currentRootId) {
  let active = null;
  for (const node of nodes.values()) {
    if (node.planeId === currentRootId) { active = node; break; }
  }
  while (active) {
    expandedNodes.add(active.boId);
    active = active.parentBoId ? nodes.get(active.parentBoId) : null;
  }
}

/**
 * Recursive tree item renderer.
 */
function renderTreeItem(node, canvas, depth, rerender) {
  const currentRoot = safeGetRootElement(canvas);
  const isActive = currentRoot?.id === node.planeId;
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedNodes.has(node.boId);

  const navigate = () => {
    const roots = safeGetRootElements(canvas);
    // Match by plane id, with a fallback to the root whose business object IS the
    // sub-process (robust against plane-id naming differences).
    const target =
      roots.find((r) => r.id === node.planeId) ||
      roots.find((r) => r.businessObject?.id === node.boId);
    if (target) {
      canvas.setRootElement(target);
    } else {
      console.warn('[SubProcessTree] root not found:', node.planeId,
        'available:', roots.map((r) => r.id));
    }
  };

  const toggle = (event) => {
    event.stopPropagation();
    if (expandedNodes.has(node.boId)) {
      expandedNodes.delete(node.boId);
    } else {
      expandedNodes.add(node.boId);
    }
    rerender();
  };

  return html`
    <li style="list-style: none;">
      <div class="subprocess-tree-row ${isActive ? 'active' : ''}" style="padding-left: ${4 + depth * 14}px">
        ${hasChildren
          ? html`<button type="button" class="subprocess-tree-toggle" onClick=${toggle}
              title=${isExpanded ? 'Einklappen' : 'Ausklappen'}>
              <i class="bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}"></i>
            </button>`
          : html`<span class="subprocess-tree-spacer"></span>`}
        <button type="button" class="subprocess-tree-item" onClick=${navigate} title=${node.name}>
          <i class="bi ${hasChildren ? 'bi-diagram-3' : 'bi-box'}"></i>
          <span class="subprocess-tree-label">${node.name}</span>
        </button>
      </div>
      ${hasChildren && isExpanded && html`
        <ul style="padding: 0; margin: 0;">
          ${node.children.map((child) => renderTreeItem(child, canvas, depth + 1, rerender))}
        </ul>
      `}
    </li>
  `;
}

/**
 * Group factory — returns a properties-panel group definition.
 *
 * Services are obtained via `injector` (not hooks) so the component never
 * crashes during mid-commandStack re-renders. All canvas calls are wrapped for
 * safety during init/destroy.
 */
export default function SubProcessTreeGroup(element, injector) {
  let elementRegistry, canvas;
  try {
    elementRegistry = injector.get('elementRegistry');
    canvas = injector.get('canvas');
  } catch {
    return null; // injector not ready
  }

  // Guard: canvas not initialized yet
  if (safeGetRootElements(canvas).length === 0) return null;

  // Only show group if there are sub-processes in the diagram
  let hasSubProcess = false;
  elementRegistry.forEach((el) => {
    if (is(el, 'bpmn:SubProcess')) {
      hasSubProcess = true;
    }
  });

  if (!hasSubProcess) return null;

  const { roots, nodes } = buildTree(elementRegistry);

  const allRoots = safeGetRootElements(canvas);
  const mainRoot = allRoots.find((r) => !r.id.endsWith('_plane')) ?? allRoots[0];

  // Keep the path to the current location open.
  expandActivePath(nodes, safeGetRootElement(canvas)?.id);

  const SubProcessTreeView = () => {
    const [, setTick] = useState(0);
    const rerender = () => setTick((t) => t + 1);

    const currentRoot = safeGetRootElement(canvas);
    const isInsideSubProcess = currentRoot && currentRoot !== mainRoot;

    const handleBackToMain = () => {
      if (mainRoot) canvas.setRootElement(mainRoot);
    };

    const expandAll = () => {
      forEachNode(roots, (n) => { if (n.children.length) expandedNodes.add(n.boId); });
      rerender();
    };
    const collapseAll = () => {
      expandedNodes.clear();
      rerender();
    };

    return html`
      <div class="subprocess-tree">
        <style>
          .subprocess-tree ul { padding: 0; margin: 0; }
          .subprocess-tree-row {
            display: flex;
            align-items: center;
            gap: 2px;
            border-radius: 3px;
            transition: background 0.1s;
          }
          .subprocess-tree-row:hover { background: #e8f0fe; }
          .subprocess-tree-row.active { background: #BBDEFB; }
          .subprocess-tree-row.active .subprocess-tree-item { font-weight: 600; }
          .subprocess-tree-toggle {
            flex: 0 0 auto;
            border: none;
            background: transparent;
            cursor: pointer;
            color: #555;
            padding: 4px;
            display: flex;
            align-items: center;
            font-size: 11px;
          }
          .subprocess-tree-toggle:hover { color: #0D4372; }
          .subprocess-tree-spacer { flex: 0 0 auto; width: 19px; }
          .subprocess-tree-item {
            display: flex;
            align-items: center;
            gap: 6px;
            flex: 1 1 auto;
            min-width: 0;
            border: none;
            background: transparent;
            padding: 5px 8px 5px 0;
            font-size: 13px;
            cursor: pointer;
            color: #333;
            text-align: left;
          }
          .subprocess-tree-label {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .subprocess-tree-toolbar {
            display: flex;
            gap: 8px;
            padding: 2px 6px 6px;
          }
          .subprocess-tree-toolbar button {
            border: none;
            background: transparent;
            cursor: pointer;
            color: #0D4372;
            font-size: 12px;
            padding: 2px 4px;
          }
          .subprocess-tree-toolbar button:hover { text-decoration: underline; }
          .subprocess-tree-back {
            display: flex;
            align-items: center;
            gap: 6px;
            width: 100%;
            border: none;
            background: #f0f4f8;
            padding: 5px 10px;
            font-size: 13px;
            cursor: pointer;
            color: #0D4372;
            text-align: left;
            border-radius: 3px;
            margin-bottom: 4px;
            font-weight: 600;
            transition: background 0.1s;
          }
          .subprocess-tree-back:hover { background: #dce8f4; }
        </style>

        ${isInsideSubProcess && html`
          <button type="button" class="subprocess-tree-back" onClick=${handleBackToMain}>
            <i class="bi bi-arrow-left"></i> Zur Hauptebene
          </button>
        `}

        <div class="subprocess-tree-toolbar">
          <button type="button" onClick=${expandAll}><i class="bi bi-arrows-expand"></i> Alle ausklappen</button>
          <button type="button" onClick=${collapseAll}><i class="bi bi-arrows-collapse"></i> Alle einklappen</button>
        </div>

        <ul style="padding: 0; margin: 0;">
          ${roots.map((node) => renderTreeItem(node, canvas, 0, rerender))}
        </ul>
      </div>
    `;
  };

  return {
    id: 'subProcessTree',
    label: 'Sub-Prozesse',
    shouldOpen: true,
    entries: [
      {
        id: 'subProcessTreeView',
        component: SubProcessTreeView,
      },
    ],
  };
}
